import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { createStytchHeadlessClient, StytchProvider } from '@stytch/nextjs';
import { SpeedInsights } from '@vercel/speed-insights/next';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ThemeProvider, useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { SWRConfig } from 'swr';

import { swrCacheProvider } from 'lib/swrCacheProvider';

import '@mercoa/react/dist/style.css';
import AuthRoutingHandler from 'components/container-components/AuthRoutingHandler';
import ErrorBoundary from 'components/container-components/ErrorBoundary';
import FallbackUI from 'components/container-components/FallbackUI';
import { RefreshSession } from 'components/container-components/RefreshSession';
import Layout from 'components/container-components/layout/Layout';
import MaintenanceMode from 'components/container-components/maintenance-mode';
import MultiFactorAuthentication from 'components/container-components/multiFactorAuthentication';
import { Toaster as AppToaster } from 'components/app-components/ui/sonner';
import MobilePullToRefresh from 'components/generic-components/MobilePullToRefresh';
import NativeExternalLinks from 'components/generic-components/NativeExternalLinks';
import SensitiveCopyAdminsProvider from 'components/container-components/SensitiveCopyAdminsProvider';
import CompanyProvider from 'context/company';
import UserProvider from 'context/user';
import { logError } from 'utils/error';
import { THEME_STORAGE_KEY } from 'utils/user';
import { patchFetchForDatadog } from 'utils/patchFetchForDatadog';
import { disableIntercomAutoClose, enableIntercomAutoClose } from 'utils/intercom';
import { useWhiteLabelFavicons } from 'hooks/useWhiteLabelFavicons';
import { useWhiteLabelRemoteConfig } from 'hooks/useWhiteLabelRemoteConfig';
import { getWhiteLabelCompanyName, isWhiteLabelEnabled } from 'utils/whiteLabelConfig';
import { canRunPosthog } from 'utils/window';
import '../styles/TheraLoader.css';
import '../styles/globals.css';
import '../styles/mercoa-dark.css';
import { MercoaProvider } from 'context/mercoa';
import { shouldFilterError } from 'utils/datadog-error';
import {
  isOnSecureUploadPage,
  isSecureUploadPath,
  isSecureUploadUrl,
  scrubSecureUploadToken,
  stripSecureUploadFragment,
} from 'utils/secureUploadPrivacy';

// Guard against Chrome auto-translate breaking React's DOM reconciliation.
// When Chrome translates a page it replaces text nodes React still holds references to,
// causing "removeChild / insertBefore: node is not a child" errors that crash the app.
// In the normal (non-translated) case the parentNode checks always pass and the original
// methods are called unchanged — no behavioural difference.
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('removeChild guard: node is not a child of this parent', child, this);
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('insertBefore guard: reference node is not a child of this parent', referenceNode, this);
      }
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

// Guard against browsers that ship an incomplete `Intl` implementation.
// Some stripped-down mobile WebViews / Chromium forks expose the
// `Intl.DateTimeFormat` and `Intl.NumberFormat` constructors but omit their
// static `supportedLocalesOf` method. react-intl's <IntlProvider> calls these
// inside @formatjs/intl's `createIntl` only to decide whether to warn about
// missing locale data, so the absent method throws
// "Intl.DateTimeFormat.supportedLocalesOf is not a function", which bubbles up
// to the ErrorBoundary and shows the Oops page. Shim it to echo back the
// requested locales: healthy browsers always use their native method, so this
// runs only on the broken ones, avoiding both the crash and noisy locale warnings.
if (typeof Intl !== 'undefined') {
  const echoSupportedLocales = (locales?: string | readonly string[]): string[] => {
    if (!locales) return [];
    return typeof locales === 'string' ? [locales] : [...locales];
  };

  if (typeof Intl.DateTimeFormat === 'function' && typeof Intl.DateTimeFormat.supportedLocalesOf !== 'function') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (Intl.DateTimeFormat as any).supportedLocalesOf = echoSupportedLocales;
  }

  if (typeof Intl.NumberFormat === 'function' && typeof Intl.NumberFormat.supportedLocalesOf !== 'function') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (Intl.NumberFormat as any).supportedLocalesOf = echoSupportedLocales;
  }
}

const Amplitude = dynamic(() => import('components/container-components/Amplitude'), {
  ssr: false,
});

const PDFJSWrapper = dynamic(() => import('components/container-components/PDFJSWrapper'), {
  ssr: false,
});

const posthog_key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const posthog_host = process.env.NEXT_PUBLIC_POSTHOG_HOST;
const stytch_public_token = process.env.NEXT_PUBLIC_STYTCH_PUBLIC_TOKEN;
const theraBackendURL = process.env.NEXT_PUBLIC_BACKEND_URL;
const datadogEnv = process.env.NEXT_PUBLIC_STYTCH_PROJECT_ENV === 'test' ? 'staging' : 'prod';
const datadogClientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
const datadogApplicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
const datadogSite = process.env.NEXT_PUBLIC_DATADOG_SITE;
const datadogBuildId = process.env.NEXT_PUBLIC_BUILD_ID;
const datadogService = 'thera-web';

const initDatadog = () => {
  // Only initialize if required environment variables are present
  if (!datadogClientToken || !datadogSite) {
    return;
  }

  try {
    datadogLogs.init({
      clientToken: datadogClientToken,
      site: datadogSite,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ['error', 'warn'],
      sessionSampleRate: 100,
      env: datadogEnv,
      service: datadogService,
      silentMultipleInit: true,
      version: datadogBuildId,
      // Filter logs before sending (catches auto-forwarded errors)
      beforeSend: (event) => {
        scrubSecureUploadToken(event);
        // Check both the main log message AND the error object's message if it exists
        const message = event.message || event.error?.message || '';
        const errorStack = event.error?.stack || '';
        // browser-logs error object can carry error type/name separately (e.g., kind/name = "AbortError")
        const errorName =
          (event.error as { kind?: string; name?: string })?.kind || (event.error as { name?: string })?.name || '';

        return !shouldFilterError(message, errorStack, errorName);
      },
    });
  } catch (error) {
    logError(error, { source: 'DatadogLogsInit' });
  }

  // Only initialize RUM if application ID is present
  if (!datadogApplicationId) {
    return;
  }

  const isSecureUploadPage = isOnSecureUploadPage();

  try {
    const allowedTracingUrls = theraBackendURL
      ? [
          // Matches any subdomain of my-api-domain.com, such as https://foo.my-api-domain.com
          // /^https:\/\/[^\/]+\.my-api-domain\.com/,
          // You can also use a function for advanced matching:
          (url: string) => url.startsWith(theraBackendURL),
        ]
      : [];

    datadogRum.init({
      clientToken: datadogClientToken,
      applicationId: datadogApplicationId,
      site: datadogSite,
      service: datadogService,
      silentMultipleInit: true,
      env: datadogEnv,
      version: datadogBuildId,
      allowedTracingUrls,
      sessionSampleRate: 100,
      // 100% replay recorded every DOM mutation on the main thread and scaled
      // with the size of the rendered tree. 10% is plenty for debugging while
      // freeing the main thread on big-customer pages.
      sessionReplaySampleRate: isSecureUploadPage ? 0 : 10,
      // Resource events were noisy with low signal; drop them.
      trackResources: false,
      trackLongTasks: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'allow',
      // Filter RUM errors before sending (reuse shared filter logic)
      beforeSend: (event) => {
        scrubSecureUploadToken(event);
        if (event.type === 'error') {
          const errorMessage = event.error?.message || '';
          const errorStack = event.error?.stack || '';
          // RUM error events carry the error "type" (e.g., "AbortError") separately from message.
          const errorName =
            (event.error as { type?: string; name?: string })?.type || (event.error as { name?: string })?.name || '';
          return !shouldFilterError(errorMessage, errorStack, errorName);
        }
        return true; // Send non-error events
      },
    });
    if (!isSecureUploadPage) datadogRum.startSessionReplayRecording();
  } catch (error) {
    logError(error, { source: 'DatadogRumInit' });
  }
};

// Initialisation only sees the first page. A visitor who reaches the upload page by client-side
// navigation loses replay as the route change starts, before the page renders, or the recorders
// capture the note and the filenames.
const stopReplayForSecureUpload = async () => {
  datadogRum.stopSessionReplayRecording();
  const posthog = (await import('posthog-js')).default;
  if (posthog.__loaded) posthog.stopSessionRecording();
};

const initPostHog = async () => {
  if (!canRunPosthog || !posthog_key || !posthog_host) return;

  const posthog = (await import('posthog-js')).default;
  // PostHog analytics only. Session replay is handled by Datadog (sampled);
  // running two recorders at full verbosity doubled main-thread snapshot cost.
  posthog.init(posthog_key, {
    api_host: posthog_host,
    disable_session_recording: isOnSecureUploadPage(),
    before_send: (event) => scrubSecureUploadToken(event),
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true, // Highly recommended as a minimum!! Referenced at https://posthog.com/docs/session-replay/privacy#input-elements
        // color: false,
        // date: false,
        // 'datetime-local': false,
        // email: false,
        // month: false,
        // number: false,
        // range: false,
        // search: false,
        // tel: false,
        // text: false,
        // time: false,
        // url: false,
        // week: false,
        // textarea: false,
        // select: false,
      },
      maskCapturedNetworkRequestFn: (request) => request,
    },
  });
};

const stytchOptions = {
  endpointOptions: {
    dfppaDomain: 'stytchauth.getthera.com',
  },
};

const stytch = createStytchHeadlessClient(stytch_public_token, stytchOptions);

// Keeps the browser-chrome color (mobile status bar) in step with the class-based
// theme toggle, which the static meta in _document cannot track.
const ThemeColorSync = () => {
  const { resolvedTheme } = useTheme();
  useEffect(() => {
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', resolvedTheme === 'dark' ? '#111111' : '#ffffff');
  }, [resolvedTheme]);
  return null;
};

export default function App({ Component, pageProps }: AppProps) {
  const [locale, setLocale] = useState('en');
  const router = useRouter();
  // Neither has a hook to scrub what it sends, and both report the page URL.
  const isSecureUploadPage = isSecureUploadPath(router.pathname);
  const wlBrandingVersion = useWhiteLabelRemoteConfig();
  const companyName = getWhiteLabelCompanyName();

  useWhiteLabelFavicons(wlBrandingVersion);

  // break out of iframe
  useEffect(() => {
    if (typeof window !== 'undefined' && window.top !== window.self) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('break_out_of_iframe')) {
        window.top.location.href = window.location.href;
      }
    }
  }, []);

  // locale
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // window.navigator.language on Linux (and some POSIX-configured systems) can return
    // "en-US@posix". The @posix part is a POSIX locale extension — valid in C/POSIX land
    // but not in BCP47, which is what Intl and IntlProvider expect. React crashes trying
    // to initialize formatting with the invalid tag, sending users to the oops page.
    // Fix: strip anything matching @\w+ from the locale string. Normal tags like
    // "en-US", "fr-FR" are completely unaffected.
    const locale = window.navigator.language.replace(/@\w+$/, '');
    setLocale(locale);
  }, []);

  useEffect(() => {
    initPostHog();
    initDatadog();
    // Patch fetch after initialization so Fern SDK failures carry request body context in Datadog.
    patchFetchForDatadog();
  }, []);

  // Track Next.js route changes with Datadog RUM
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      // Track route changes as RUM views
      if (datadogRum?.getInternalContext?.()) {
        datadogRum.startView({ name: stripSecureUploadFragment(url) });
      }
    };
    const stopReplayBeforeSecureUpload = (url: string) => {
      if (isSecureUploadUrl(url)) stopReplayForSecureUpload();
    };

    // Track initial page load
    handleRouteChange(router.asPath);

    // Subscribe to route changes
    router.events.on('routeChangeStart', stopReplayBeforeSecureUpload);
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', stopReplayBeforeSecureUpload);
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, router.asPath]);

  // Enable Intercom auto-close functionality
  useEffect(() => {
    // Wait a bit for Intercom to load
    const timer = setTimeout(() => {
      enableIntercomAutoClose();
    }, 1000);

    return () => {
      clearTimeout(timer);
      disableIntercomAutoClose();
    };
  }, []);

  useEffect(() => {
    // Network error handler
    const handleNetworkError = (event: ErrorEvent | string) => {
      const errorMessage = typeof event === 'string' ? event : event.message;
      const error = typeof event === 'string' ? new Error(event) : event;

      if (errorMessage && (errorMessage.includes('SSL') || errorMessage.includes('CERT'))) {
        const customError = new Error('User might be experiencing a DNS issue');
        logError(customError, { source: 'DNS Error' });
      }

      // Handle connection refused separately to ensure it's reported
      if (error instanceof Error && error.message.includes('Connection refused')) {
        const customError = new Error('Domain blocked by user network - connection refused');
        logError(customError, { source: 'Network Block' });
      }
    };

    // Handle general window errors
    window.addEventListener('error', handleNetworkError);

    // Handle route change errors
    router.events.on('routeChangeError', handleNetworkError);

    // Cleanup listeners on component unmount
    return () => {
      window.removeEventListener('error', handleNetworkError);
      router.events.off('routeChangeError', handleNetworkError);
    };
  }, [router]);

  return (
    // Dark mode is opt-in via the navbar toggle (default light, no system sync yet).
    // White-label tenants stay locked to light until they supply dark logo assets.
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      storageKey={THEME_STORAGE_KEY}
      forcedTheme={isWhiteLabelEnabled() ? 'light' : undefined}
      disableTransitionOnChange
    >
      <ThemeColorSync />
      <ErrorBoundary fallback={<FallbackUI />}>
        <SWRConfig value={{ provider: swrCacheProvider }}>
          <AppToaster position="top-center" duration={5000} />
          <MobilePullToRefresh />
          <NativeExternalLinks />
          <Head>
            <title>{`${companyName} | Payroll + AP/AR for Global Teams`}</title>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1.0, maximum-scale=2.0, user-scalable=yes, viewport-fit=cover"
            />
            {/* Warm the TCP/TLS handshake to the BE in parallel with HTML parse
                so the first API call doesn't pay the connection setup cost. */}
            {theraBackendURL && <link rel="preconnect" href={theraBackendURL} crossOrigin="anonymous" />}
          </Head>
          <IntlProvider locale={locale} defaultLocale="en">
            <StytchProvider stytch={stytch}>
              <UserProvider>
                <SensitiveCopyAdminsProvider>
                  <MultiFactorAuthentication>
                    <CompanyProvider>
                      <MercoaProvider>
                        <AuthRoutingHandler>
                          <MaintenanceMode>
                            <Layout>
                              <PDFJSWrapper>
                                <Component {...pageProps} />
                              </PDFJSWrapper>
                              {!isSecureUploadPage && (
                                <>
                                  <Amplitude />
                                  <SpeedInsights sampleRate={0.6} />
                                </>
                              )}
                            </Layout>
                          </MaintenanceMode>
                        </AuthRoutingHandler>
                      </MercoaProvider>
                    </CompanyProvider>
                  </MultiFactorAuthentication>
                </SensitiveCopyAdminsProvider>
              </UserProvider>
              <RefreshSession />
            </StytchProvider>
          </IntlProvider>
        </SWRConfig>
      </ErrorBoundary>
    </ThemeProvider>
  );
}
